import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Drawer, IconButton, useTheme } from '@mui/material';
import { useOpenSidePanel } from '@/contexts/side-panel-context';
import { HugeiconsIcon } from '@hugeicons/react';
import { MultiplicationSignIconStroke, PencilEdit02IconStroke } from '@/components/icons';
import { useSession } from '../../contexts/session-context';

const HELP_CMS_BASE_URL = 'https://help-cms.hd.pics';
const HELP_CMS_ORIGIN = new URL(HELP_CMS_BASE_URL).origin;
const INITIAL_DRAWER_HEIGHT = 56;
const MAX_DRAWER_HEIGHT = '90vh';
const HEIGHT_TRANSITION = 'height 0.3s ease-out';

const drawerControlButtonSx = {
    width: 40,
    height: 40,
    bgcolor: 'var(--background-content, #fff)',
    color: 'var(--primary-text, rgba(0, 0, 0, 0.87))',
    '&:hover': {
        bgcolor:
            'color-mix(in srgb, var(--primary-text, #000) 8%, var(--background-content, #fff))',
        color: 'var(--primary-text, rgba(0, 0, 0, 0.87))'
    }
} as const;

function buildHelpTopicUrl(path: string, isDarkMode: boolean, userType?: number) {
    const url = `${HELP_CMS_BASE_URL}/${path}`;
    const parts: string[] = [];
    if (userType != null) {
        parts.push(`userType=${userType}`);
    }
    if (isDarkMode) {
        parts.push('dark');
    }
    return parts.length > 0 ? `${url}?${parts.join('&')}` : url;
}

interface HelpTopicDrawerProps {
    open: boolean;
    preload?: boolean;
    onClose: () => void;
    helpTopicPath: string;
}

function isHelpCmsHeightMessage(data: unknown): data is { type: 'helpcms:height'; height: number } {
    return (
        typeof data === 'object' &&
        data !== null &&
        (data as { type?: string }).type === 'helpcms:height' &&
        typeof (data as { height?: unknown }).height === 'number' &&
        Number.isFinite((data as { height: number }).height) &&
        (data as { height: number }).height > 0
    );
}

export function HelpTopicDrawer({
    open,
    preload = false,
    onClose,
    helpTopicPath
}: HelpTopicDrawerProps) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { isAdmin, userType } = useSession();
    const openSidePanel = useOpenSidePanel();
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const shouldLoadIframe = open || preload;
    const helpTopicUrl = useMemo(
        () => buildHelpTopicUrl(helpTopicPath, isDarkMode, userType),
        [helpTopicPath, isDarkMode, userType]
    );

    useEffect(() => {
        setContentHeight(null);
    }, [helpTopicPath, isDarkMode, userType]);

    useEffect(() => {
        if (!shouldLoadIframe) {
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== HELP_CMS_ORIGIN) {
                return;
            }

            if (!isHelpCmsHeightMessage(event.data)) {
                return;
            }

            setContentHeight(Math.round(event.data.height));
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [shouldLoadIframe, helpTopicUrl]);

    const drawerHeightPx = contentHeight ?? INITIAL_DRAWER_HEIGHT;

    const handleEditHelpTopic = useCallback(() => {
        window.open(
            `${HELP_CMS_BASE_URL}/admin/edit/${helpTopicPath}`,
            '_blank',
            'noopener,noreferrer'
        );
    }, [helpTopicPath]);

    return (
        <Drawer
            anchor="bottom"
            open={open}
            keepMounted
            onClose={onClose}
            /* UX-827 QA defect 2. The drawer is a portal, but React bubbles synthetic events
             * through portals along the COMPONENT tree, and `Info` renders this drawer inside the
             * header the icon sits in. So a click on Close, Edit or the backdrop reached
             * `PaperCollapse`'s header `onClick` and expanded the very section help was opened
             * from. The icon's own click already stops propagation; these are the drawer's own
             * clicks, so they stop here rather than in each of the 18 affected headers. Escape
             * never bubbled and is unchanged. */
            onClick={event => event.stopPropagation()}
            /* UX-827 QA defect 1. MUI stacks Drawer at `zIndex.drawer` (1200) and Dialog /
             * Popover / Menu at `zIndex.modal` (1400 in this theme), so an icon inside the Add
             * Time Off dialog or the Team Members Display menu opened its help UNDERNEATH them —
             * dimmed, with the Close button behind the modal's backdrop. Help must be openable
             * from inside anything, so it sits above the modal layer; `+ 2` rather than `+ 1`
             * clears the profile menu, which already claims `modal + 1`. Still below snackbar
             * (1500) and tooltip (1600). */
            sx={{ zIndex: theme.zIndex.modal + 2 }}
            /* API-482. The ONLY drawer in the app that uses MUI's Drawer directly rather than the
             * shared `components/drawer` wrapper, so it does not inherit the wrapper's fix and had
             * to be given the same one. See the long note there for why this is conditional rather
             * than a blanket `disableEnforceFocus`. */
            disableEnforceFocus={openSidePanel !== null}
            slotProps={{
                paper: {
                    sx: {
                        width: '100%',
                        maxWidth: 1200,
                        mx: 'auto',
                        borderTopLeftRadius: 'var(--container-radius, 4px)',
                        borderTopRightRadius: 'var(--container-radius, 4px)',
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        overflow: 'hidden',
                        background: 'var(--background-content, #fff)'
                    }
                }
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: `${drawerHeightPx}px`,
                    maxHeight: MAX_DRAWER_HEIGHT,
                    overflow: 'hidden',
                    transition: HEIGHT_TRANSITION
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        display: 'flex',
                        gap: 1
                    }}
                >
                    {isAdmin && (
                        <IconButton
                            onClick={handleEditHelpTopic}
                            aria-label="Edit help topic"
                            sx={drawerControlButtonSx}
                        >
                            <HugeiconsIcon
                                icon={PencilEdit02IconStroke}
                                size={20}
                                strokeWidth={1.5}
                                color="currentColor"
                            />
                        </IconButton>
                    )}
                    <IconButton
                        onClick={onClose}
                        aria-label="Close help"
                        sx={drawerControlButtonSx}
                    >
                        <HugeiconsIcon
                            icon={MultiplicationSignIconStroke}
                            size={20}
                            strokeWidth={1.5}
                            color="currentColor"
                        />
                    </IconButton>
                </Box>
                <Box
                    component="iframe"
                    title={`Help: ${helpTopicPath}`}
                    src={shouldLoadIframe ? helpTopicUrl : undefined}
                    sx={{
                        border: 0,
                        width: '100%',
                        height: '100%',
                        display: shouldLoadIframe ? 'block' : 'none'
                    }}
                />
            </Box>
        </Drawer>
    );
}
